// src/features/pages/admin/RolesPermissionsPage.tsx
// Tenant-facing dynamic RBAC (Model A). Two capabilities, both gated on
// `user:role:assign`:
//   1. Custom role builder — create/edit/delete tenant-scoped custom roles from
//      the tenant's ALLOWED permission catalog.
//   2. Role assignment — assign system + custom roles to people in the tenant.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ShieldCheck } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/features/shared/components/ui/tabs';
import { RequirePermission } from '@/rbac/RequirePermission';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  getCatalog,
  getRoles,
  type RbacCatalog,
  type RolesResponse,
} from '@/services/rbacService';
import { RolesTab } from './roles/RolesTab';
import { AssignmentsTab } from './roles/AssignmentsTab';

const EMPTY_ROLES: RolesResponse = { systemRoles: [], customRoles: [] };

const RolesPermissionsInner: React.FC = () => {
  const [catalog, setCatalog] = useState<RbacCatalog>({ services: [] });
  const [roles, setRoles] = useState<RolesResponse>(EMPTY_ROLES);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [cat, rls] = await Promise.all([getCatalog(), getRoles()]);
      setCatalog(cat);
      setRoles(rls);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not load roles & permissions'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-[12px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300 shrink-0">
          <ShieldCheck className="w-5 h-5" />
        </span>
        <div>
          <h1 className="text-h2 text-charcoal">Roles &amp; permissions</h1>
          <p className="text-muted-foreground mt-1">
            Build custom roles for your company and assign them to your people.
          </p>
        </div>
      </div>

      <Tabs defaultValue="roles">
        <TabsList>
          <TabsTrigger value="roles">Roles</TabsTrigger>
          <TabsTrigger value="assignments">Assignments</TabsTrigger>
        </TabsList>
        <TabsContent value="roles" className="mt-6">
          <RolesTab catalog={catalog} roles={roles} loading={loading} onReload={load} />
        </TabsContent>
        <TabsContent value="assignments" className="mt-6">
          <AssignmentsTab roles={roles} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export const RolesPermissionsPage: React.FC = () => (
  <RequirePermission perm="user:role:assign">
    <RolesPermissionsInner />
  </RequirePermission>
);

export default RolesPermissionsPage;
